import * as React from 'react';

import { faCameraCctv, faHouseBlank, faToilet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useUpdateParking } from 'src/features/parkingManagement/api/updateParking.ts';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking.ts';
import { FEATURES } from 'src/features/parkingManagement/types/parking.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock.tsx';
import { toast } from 'src/registry/hooks/UseToast.ts';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { Separator } from 'src/registry/ui/Separator.tsx';
import { useTitleStore } from 'src/stores/useTitleStore.ts';
import { generalErrorMessages, generalLabelMessages } from 'src/utils/messages.ts';

const Amenities = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const [amenities, setAmenities] = React.useState<FEATURES[]>([]);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        setAmenities(data?.features);
        return () => removeTitle();
    }, [data?.features, formatMessage]);

    const amenitiesTranslations = {
        [FEATURES.WC]: parkingManagementMessages.wc,
        [FEATURES.UNDERGROUND]: parkingManagementMessages.underground,
        [FEATURES.VIDEO_SURVEILLANCE]: parkingManagementMessages.videoSurveillance,
    };
    const amenitiesIcons = {
        [FEATURES.WC]: faToilet,
        [FEATURES.UNDERGROUND]: faHouseBlank,
        [FEATURES.VIDEO_SURVEILLANCE]: faCameraCctv,
    };

    const { mutate: updateAmenities } = useUpdateParking({
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    const handleCheckbox = (value: FEATURES) => {
        const updated = amenities.includes(value) ? amenities.filter((a) => a !== value) : [...amenities, value];

        setAmenities(updated);
        updateAmenities({
            parkingId: parkingId,
            features: updated,
        });
    };

    return (
        <>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>{formatMessage(parkingManagementMessages.amenities)}</GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    {formatMessage(parkingManagementMessages.amenitiesSubtitle)}
                </GeneralBlockSubtitle>
            </div>
            <Separator />
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
                {Object.entries(FEATURES).map(([key, value]) => {
                    return (
                        <div className="flex w-full flex-row items-center justify-start" key={key}>
                            <Checkbox
                                key={key}
                                value={value}
                                checked={amenities.includes(value)}
                                onCheckedChange={() => handleCheckbox(value)}
                                className="border-primary-600 size-4 border"
                            />
                            <div className="ml-2 whitespace-nowrap">{formatMessage(amenitiesTranslations[value])}</div>
                            <FontAwesomeIcon icon={amenitiesIcons[value]} className="text-primary-600 ml-2" />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default Amenities;
